import { useState } from 'react'
import { isAxiosError } from 'axios'
import { Dialog, DialogActions, DialogContent, Menu } from '@mui/material'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { Field, Form, Formik } from 'formik'
import { DeleteIcon, MoreVerticalIcon, PlusIcon } from '@dhl-ui/icons'
import { DialogTitleClosable, type GridColDef, Pagination } from '@dhl/components'
import { ConfirmationDialog } from '@dhl/confirmation-dialog'
import { SelectInput } from '@dhl/formik'
import {
  type IntegratorDto,
  type OrganisationCccIntegratorDto,
  useCreateOrganisationIntegrator,
  useDeleteOrganisationIntegrator,
  useGetIntegrators,
  useGetOrganisationIntegrators,
} from '@dhl/mdp-api'
import { Body1, Box, Button, Card, Checkbox, Divider, Grid, H5, IconButton, MenuItem } from '@dhl-ui/components'
import { AdminDataGrid, useNotificationMessage } from '~/common'
import { getErrorMessage } from '~/common/util/axiosUtil'
import type { Organisation } from '~/modules/organisations/model'

type Props = {
  organisation: Organisation
}

export const IntegratorsCard = ({ organisation }: Props) => {
  const { t } = useTranslation()
  const { showError, showSuccess } = useNotificationMessage()
  const [isVisible, setIsVisible] = useState<boolean>(false)
  const [page, setPage] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(10)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false)
  const [selectedIntegrator, setSelectedIntegrator] = useState<OrganisationCccIntegratorDto | undefined>(undefined)
  const [anchorEl, setAnchorEl] = useState(null)
  const menuOpen = Boolean(anchorEl)
  const { id: organisationId } = organisation

  const { mutateAsync: createOrganisationIntegrator } = useCreateOrganisationIntegrator()
  const { mutateAsync: deleteOrganisationIntegrator } = useDeleteOrganisationIntegrator()
  const {
    data: organisationIntegrators = [],
    refetch,
    isFetching: isFetchingOrganisationIntegrators,
  } = useGetOrganisationIntegrators(organisationId)
  const { data } = useGetIntegrators({ skip: 0, max: 1000 })
  const integratorList = data?.content ?? []

  const validationSchema = () =>
    Yup.object()
      .shape({
        integratorId: Yup.string().default('').required(t('admin.organisationsPage.integrators.validationMessage')),
      })
      .defined()

  const onSubmit = (formFields: OrganisationCccIntegratorDto) => {
    createOrganisationIntegrator(
      { id: organisationId, data: formFields },
      {
        onSuccess: async () => {
          showSuccess(t('admin.organisationsPage.integrators.addSuccessMessage'))
          await refetch()
          handleClose()
        },
        onError: (error) => {
          const message = getErrorMessage(error, t('admin.organisationsPage.integrators.errorMessage'))
          showError(message)
        },
      }
    )
  }

  const addButtonClick = () => {
    if (organisation.ccc?.billingUnitId) {
      setIsVisible(true)
    } else {
      showError(t('admin.organisationsPage.integrators.cccInfoRequired'))
    }
  }

  const renderActionsCell = (integrator: OrganisationCccIntegratorDto) => {
    return (
      <div key={`id_${integrator.organisationId}`}>
        <IconButton
          aria-controls={menuOpen ? 'more-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={menuOpen ? 'true' : undefined}
          aria-label="select integrator"
          id="more-button"
          onClick={(event: any) => {
            setAnchorEl(event.currentTarget)
            setSelectedIntegrator(integrator)
          }}
        >
          <MoreVerticalIcon />
        </IconButton>
      </div>
    )
  }

  const renderAllowed = (value: never, row: OrganisationCccIntegratorDto) =>
    t(`admin.${row.isAllowedToGetAll ? 'yes' : 'no'}`)

  const columns: GridColDef<OrganisationCccIntegratorDto>[] = [
    {
      field: 'integratorName',
      headerName: t('admin.organisationsPage.integrators.integratorName'),
      flex: 1,
      sortable: true,
    },
    {
      field: 'isAllowedToGetAll',
      headerName: t('admin.organisationsPage.integrators.isAllowed'),
      flex: 1,
      valueGetter: renderAllowed,
      sortable: true,
    },
    {
      field: 'actions',
      headerName: t('admin.organisationsPage.integrators.actions'),
      flex: 1,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: ({ row }) => renderActionsCell(row),
    },
  ]

  const deleteIntegrator = () => {
    deleteOrganisationIntegrator(
      { id: organisationId, integratorId: selectedIntegrator!.integratorId },
      {
        onSuccess: () => {
          showSuccess(t('admin.organisationsPage.integrators.deleteSuccessMessage'))
          handleDeleteConfirmClose()
          refetch()
        },
        onError: (error) => {
          if (isAxiosError(error)) {
            showError(error.message)
          }
        },
      }
    )
  }

  const handleClose = () => {
    setSelectedIntegrator(undefined)
    setIsVisible(false)
  }

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true)
  }

  const handleDeleteConfirmClose = () => {
    setSelectedIntegrator(undefined)
    setShowDeleteConfirm(false)
    setAnchorEl(null)
  }

  const initialValues: OrganisationCccIntegratorDto = {
    integratorId: '',
    isAllowedToGetAll: true,
    organisationId,
  }

  if (isFetchingOrganisationIntegrators) return null

  return (
    <>
      <Card>
        <Card.Header
          title={t('admin.organisationsPage.integrators.header')}
          endNode={
            <Button
              size="small"
              data-testid="add-accountNumber"
              color="primary"
              variant="contained"
              startIcon={<PlusIcon />}
              onClick={() => addButtonClick()}
            >
              {t('admin.organisationsPage.integrators.addIntegrator')}
            </Button>
          }
        />
        <Card.Content>
          <AdminDataGrid<OrganisationCccIntegratorDto>
            getRowId={(row) => row.integratorId}
            initialState={{
              sorting: {
                sortModel: [{ field: 'integratorName', sort: 'asc' }],
              },
            }}
            onSortModelChange={() => {
              setPage(0)
            }}
            sx={{ '& .MuiDataGrid-cell': { cursor: 'default' } }}
            data-testid="account-table"
            rowCount={organisationIntegrators.length}
            rows={organisationIntegrators}
            columns={columns}
            autoHeight
            pagination
            paginationMode="client"
            paginationModel={{
              page,
              pageSize,
            }}
            disableRowSelectionOnClick
            disableColumnFilter
            hideFooter
          />
        </Card.Content>
        <Box px={2} py={1}>
          <Pagination
            page={page}
            count={organisationIntegrators.length}
            onPageChange={(_, page) => setPage(page)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={setPageSize}
          />
        </Box>

        {isVisible && (
          <Formik initialValues={initialValues} onSubmit={onSubmit} validateOnMount validationSchema={validationSchema}>
            {({ isValid, handleSubmit }) => {
              return (
                <Form>
                  <Dialog data-testid="account-form" open={true} fullWidth maxWidth="sm">
                    <DialogTitleClosable onClose={handleClose}>
                      <Box>
                        <H5>{t('admin.organisationsPage.integrators.addIntegrator')}</H5>
                      </Box>
                    </DialogTitleClosable>
                    <Divider variant="fullWidth" />
                    <DialogContent>
                      <Box mt={3} mb={2}>
                        <Grid direction="column" spacing={1} container>
                          <Grid>
                            <Field fullWidth name="integratorId" component={SelectInput} label={'Integrator'}>
                              {integratorList
                                .filter(
                                  (i: IntegratorDto) => !organisationIntegrators.some((oi) => oi.integratorId === i.id)
                                )
                                .map((item: { name: string; id?: string }) => (
                                  <MenuItem key={item.id} value={item.id}>
                                    {item?.name}
                                  </MenuItem>
                                ))}
                            </Field>
                          </Grid>
                          <Grid>
                            <Checkbox
                              name="isAllowedToGetAll"
                              label={t('admin.organisationsPage.integrators.checkboxAllowed').toString()}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    </DialogContent>
                    <Divider variant="fullWidth" />
                    <DialogActions>
                      <Box mt={0} mb={0} gap={1} display="flex" justifyContent="flex-end">
                        <Button color="primary" onClick={handleClose}>
                          {t('admin.accountNumbers.cancel')}
                        </Button>
                        <Button variant="contained" color="primary" disabled={!isValid} onClick={() => handleSubmit()}>
                          {t('admin.organisationsPage.integrators.addIntegrator')}
                        </Button>
                      </Box>
                    </DialogActions>
                  </Dialog>
                </Form>
              )
            }}
          </Formik>
        )}
      </Card>

      <Menu
        id="more-menu"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{
          'aria-labelledby': 'more-button',
        }}
      >
        <MenuItem onClick={handleDeleteClick} icon={<DeleteIcon />} isDestructive>
          {t('admin.accountNumbers.delete')}
        </MenuItem>
      </Menu>

      {showDeleteConfirm && (
        <ConfirmationDialog
          open={true}
          titleText={t('admin.organisationsPage.integratorDangerZoneDialogs.header')}
          acceptText={t('admin.organisationsPage.integratorDangerZoneDialogs.deleteButton')}
          rejectText={t('admin.organisationsPage.integratorDangerZoneDialogs.cancelButton')}
          onAccept={deleteIntegrator}
          onReject={handleDeleteConfirmClose}
        >
          <Body1>{t('admin.organisationsPage.integratorDangerZoneDialogs.message')}</Body1>
        </ConfirmationDialog>
      )}
    </>
  )
}
